import React from 'react'
import { getRelativeBasePath } from '../../../global/environments/path.js'
import * as StringFormatter from '../../../global/formatters/string-formatter.js'
import { ETF_FUND, ETF_INDEX_FUND, MODEL_PORTFOLIOS } from './FundTypes.js'

/*
 * FundUtility.js - helper functions related to Fund data objects.
 * fund.miscellaneousDetails.layoutID:
 *   8 - Columbia Government Money Market Fund, CGMXX
 */

export function hasLayoutId(fund, layoutIds) {
  let result = false

  // this comparison can't be strict because the awesome service returns strings or numbers. #sadness
  layoutIds.forEach((id) => {
    if (fund.miscellaneousDetails.layoutID == id) {
      result = true
    }
  })

  return result
}

export function isDisplayFullHoldingsForFund(fund) {
  if (!fund.miscellaneousDetails) {
    return false
  }

  return hasLayoutId(fund, [8, 17, 19, 21, 37, 38])
}

export function onHideNavFieldForFund(fund, field) {
  const isEtf = fund.fundGroup === ETF_FUND
  const isEtfIndex = fund.fundGroup === ETF_INDEX_FUND
  const isModelPortfolio = fund.fundGroup === MODEL_PORTFOLIOS
  const portfolioCharacteristicsIssueCount =
    fund?.portfolioCharsDetail?.portfolioCharacteristics?.find(
      ({ code }) => code === 'ISSUE_COUNT'
    )
  const hasEtfIndexPortfolioCharData =
    isEtfIndex && portfolioCharacteristicsIssueCount?.indexValue !== null

  let hideLayoutIDs = []
  let shouldBeHidden = false
  let result = false

  if (!fund.miscellaneousDetails) {
    return false
  }

  if (field === 'overview') {
    hideLayoutIDs = [17]
  } else if (field === 'performance') {
    hideLayoutIDs = [36, 19, 37, 38]

    result = !fund?.totalReturnPerformances && isModelPortfolio
  } else if (field === 'ratings-risk') {
    // these are layoutIDs where ratings and risk should be hidden.
    hideLayoutIDs = [8, 16, 17, 36, 19, 37, 38]

    result = result || isModelPortfolio
  } else if (field === 'fees-expenses') {
    // these are layoutIDs where fees and expenses should be hidden.
    hideLayoutIDs = [15, 23, 19, 31, 32, 33, 34, 35, 36, 37, 38]

    result = result || isEtf || isModelPortfolio // if fund is an ETF fund, also hide it.
  } else if (field === 'portfolio-details') {
    hideLayoutIDs = [16]

    result = result || (isEtfIndex && !hasEtfIndexPortfolioCharData)
  } else if (field === 'capital-gains') {
    hideLayoutIDs = [15, 16, 17, 19, 23, 36, 37, 38]

    result = result || isModelPortfolio
  } else if (field === 'literature') {
    hideLayoutIDs = []
  } else if (field === 'related-products') {
    // these are layoutIDs where related products should be hidden.
    // Note: legacy logic says when related product investor goal content exists, show it.
    shouldBeHidden = !fund.relatedProducts || !fund.relatedProducts.investorGoal
    hideLayoutIDs = [8, 17, 19, 36, 37]
  }

  result = result || shouldBeHidden || hasLayoutId(fund, hideLayoutIDs)

  return result
}

export function isDisplayNoMessageData(fund, presentation) {
  if (!fund.presentationLayouts) {
    return false
  }

  for (const presentationLayout of fund.presentationLayouts) {
    if (
      presentation === presentationLayout.name &&
      presentationLayout.value === 'NODATA'
    ) {
      return true
    }
  }

  return false
}

export function isPresented(fund, presentation) {
  if (!fund.presentationLayouts) {
    return false
  }

  for (const presentationLayout of fund.presentationLayouts) {
    if (
      presentation === presentationLayout.name &&
      presentationLayout.value === 'ACTIVE'
    ) {
      return true
    }
  }

  return false
}

/**
 * Hack for now to see if top level objects are all null, then the whole object is null
 * data clean up will make this not necessary
 * @param obj
 * @returns {boolean}
 */
export function isNull(obj) {
  if (obj && Array.isArray(obj)) {
    if (obj.length < 1) {
      return true
    }
  }
  for (const i in obj) {
    if (obj[i]) {
      return false
    }
  }
  return true
}

// swapIndexPercentValue is a hack because data request sometimes has fundPercent stored in the indexPercent column.
export function swapIndexPercentValue(details, fieldName) {
  let hasFundPercent = false

  if (!details || !Array.isArray(details)) {
    return null
  }

  fieldName = fieldName || 'fundPercent'

  details.forEach((v) => {
    if (!hasFundPercent) {
      hasFundPercent = !!v[fieldName]
    }
  })

  if (!hasFundPercent) {
    details.forEach((v) => {
      v[fieldName] = v.indexPercent
      v.indexPercent = null
    })
  }

  return details
}

export function generateFundDetailUrl(fund, fundPath, fundClass, cusip) {
  const basePath = getRelativeBasePath()
  const symbolOrClass = fund.symbol ? fund.symbol : `Class-${fundClass}`

  return `${basePath}/investment-products/${fundPath}/${StringFormatter.urlSafe(
    fund.fundName
  )}/${symbolOrClass}/details/?cusip=${cusip}`
}

export function morningstarStarIconHelper(starsValue) {
  const starsArr = []
  let i = 0

  while (i < starsValue) {
    starsArr.push(
      <i key={`star${i}`} aria-hidden="true" role="presentation">
        ★
      </i>
    )
    i++
  }

  return starsValue > 0 ? starsArr : starsValue
}

export function plusMinusLabelHelper(numberValue, preLabel, postLabel) {
  let output = ''

  if (numberValue === null) {
    output = '-'
  } else if (isNaN(numberValue)) {
    output = numberValue
  } else if (Number(numberValue) < 0) {
    output += '-'
    output += StringFormatter.safe(preLabel)
    output += numberValue.substring(1)
    output += StringFormatter.safe(postLabel)
  } else if (Number(numberValue) > 0) {
    output += '+'
    output += StringFormatter.safe(preLabel)
    output += numberValue
    output += StringFormatter.safe(postLabel)
  } else if (numberValue == 0) {
    output += StringFormatter.safe(preLabel)
    output += numberValue
    output += StringFormatter.safe(postLabel)
  }

  return output
}

export function upDownArrowLabelHelper(numberValue) {
  let output = ''

  if (numberValue === null) {
    output = ''
  } else if (isNaN(numberValue)) {
    output = ''
  } else if (numberValue < 0) {
    output = (
      <span className="Icon u-textColorAlert u-marginLeft1gu u-textSizeMsrp1">
        ⬇
      </span>
    )
  } else if (numberValue > 0) {
    output = (
      <span className="Icon u-textColorConfirm u-marginLeft1gu u-textSizeMsrp1">
        ⬆
      </span>
    )
  } else if (numberValue == 0) {
    output = ''
  }

  return output
}

export function sortHoldings(holdings, sortOrder, sortField) {
  if (!Array.isArray(holdings) || !sortOrder || !sortField) {
    return holdings
  }

  return holdings.sort((a, b) => {
    const order = sortOrder === 'asc' ? 1 : -1
    const value = a[sortField]
    const value2 = b[sortField]

    // handle string alphanumeric values normally, case insensitive.
    if (typeof value === 'string' && typeof value2 === 'string') {
      return (
        order *
        value.localeCompare(value2, { sensitivity: 'base' }, { numeric: true })
      )
    }

    // we have to check both values because our data is mixed;
    // sometimes they're strings and sometimes they're numbers.
    if (typeof value === 'string' || typeof value2 === 'string') {
      const values = StringFormatter.convertStringsToNumbers([value, value2])

      return order * (values[0] < values[1] ? -1 : 1)
    } else {
      return order * (value < value2 ? -1 : 1)
    }
  })
}

export function cleanUpEtfRegMessData(premDiscMessageRaw) {
  // example resource bundle format:
  //   consecutiveDaysMin: 8
  //   cusip-1976xxxxx: 'disclaimer content'
  const premDiscMessage = {
    funds: [],
  }

  premDiscMessageRaw.forEach((key) => {
    const prop = Object.keys(key)[0]

    if (prop.includes('cusip')) {
      premDiscMessage.funds.push({
        cusip: prop.split('-')[1],
        message: key[prop],
      })
    }
  })

  return premDiscMessage
}

export function getEtfPremDiscMessage(fundDetails, etfMessage) {
  if (etfMessage && etfMessage.funds.length > 0) {
    const etfPremDiscMessage = {}

    etfMessage.funds.forEach((fund) => {
      if (fund.cusip === fundDetails.cusip) {
        etfPremDiscMessage.message = fund.message
      }
    })

    return etfPremDiscMessage
  } else {
    return null
  }
}

export function hasLiteratureContent(literature) {
  return (
    literature &&
    ((literature.productDocuments &&
      literature.productDocuments.value &&
      literature.productDocuments.value.length > 0) ||
      (literature.regulatoryDocuments &&
        literature.regulatoryDocuments.value &&
        literature.regulatoryDocuments.value.length > 0))
  )
}

function determineInvestorGoalsType(investorGoals) {
  let isGenerateIncome = false

  investorGoals &&
    investorGoals.value &&
    investorGoals.value.forEach((index) => {
      if (index.displayDesc === 'Generate Income') {
        isGenerateIncome = true
      }
    })

  return isGenerateIncome
}

export function getFundGoalType(fund) {
  const isMultiManagerFund = fund.fundHeader
    ? fund.fundHeader.multiManager
    : false
  const isGenerateIncome =
    fund.fundGroup === ETF_INDEX_FUND
      ? false
      : determineInvestorGoalsType(fund.fundHeader.investorGoals)
  const fundType = isMultiManagerFund
    ? 'multi-managed'
    : isGenerateIncome
      ? 'generate-income'
      : 'generic'

  return fundType
}
